import { useState, useEffect } from 'react'
import { X, RefreshCw, Search, Trash2 } from 'lucide-react'
import { useAuth } from '@clerk/clerk-react'
import { mutate } from 'swr'

interface Cast {
  name: string
  character: string
  profile_path?: string
}

interface Crew {
  name: string
  job: string
  profile_path?: string
}

interface Title {
  id: string
  name: string
  nameJa?: string
  posterUrl?: string
  backdropUrl?: string
  viewCount: number
  mediaType: 'movie' | 'tv'
  genres: string[]
  firstWatched: string
  lastWatched: string
  tmdbId?: number
  overview?: string
  voteAverage?: number
  voteCount?: number
  releaseDate?: string
  runtime?: number
  status?: string
  cast?: Cast[]
  crew?: Crew[]
  trailerKey?: string
  numberOfSeasons?: number
  numberOfEpisodes?: number
}

interface TitleDetailModalProps {
  title: Title
  onClose: () => void
  onOpenSearch: () => void
}

export function TitleDetailModal({ title: initialTitle, onClose, onOpenSearch }: TitleDetailModalProps) {
  const { getToken } = useAuth()
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [title, setTitle] = useState<Title>(initialTitle)

  // Fetch full title details when modal opens
  useEffect(() => {
    const fetchDetails = async () => {
      setIsLoading(true)
      try {
        const token = await getToken()
        const res = await fetch(`/api/v1/titles/${initialTitle.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!res.ok) {
          throw new Error('Failed to fetch details')
        }

        const data = await res.json()
        setTitle(data)
      } catch (error) {
        console.error('Failed to fetch title details:', error)
        // Keep using initial title data if fetch fails
      } finally {
        setIsLoading(false)
      }
    }

    fetchDetails()
  }, [initialTitle.id, getToken])

  const handleRefresh = async () => {
    if (!title.tmdbId) {
      alert('TMDb IDが設定されていません。検索から再設定してください。')
      return
    }

    setIsRefreshing(true)
    try {
      const token = await getToken()
      const res = await fetch(`/api/v1/titles/${title.id}/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!res.ok) {
        throw new Error('Failed to refresh')
      }

      // Refresh the titles list and update local state
      mutate(['/api/v1/titles'])

      // Fetch updated details
      const detailsRes = await fetch(`/api/v1/titles/${title.id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (detailsRes.ok) {
        const updatedData = await detailsRes.json()
        setTitle(updatedData)
      }

      alert('情報を更新しました')
    } catch (error) {
      console.error('Failed to refresh:', error)
      alert('情報の更新に失敗しました')
    } finally {
      setIsRefreshing(false)
    }
  }

  const handleDelete = async () => {
    if (!confirm(`「${title.name}」を削除してもよろしいですか？`)) {
      return
    }

    try {
      const token = await getToken()
      const res = await fetch(`/api/v1/titles/${title.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (!res.ok) {
        throw new Error('Failed to delete')
      }

      // Refresh the titles list
      mutate(['/api/v1/titles'])

      alert('削除しました')
      onClose()
    } catch (error) {
      console.error('Failed to delete:', error)
      alert('削除に失敗しました')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="relative bg-background rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-muted z-10"
        >
          <X className="h-5 w-5" />
        </button>

        {title.backdropUrl && (
          <div className="relative h-64 w-full overflow-hidden rounded-t-lg">
            <img
              src={title.backdropUrl}
              alt={title.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
          </div>
        )}

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">読み込み中...</p>
            </div>
          ) : (
            <>
          <div className="flex gap-4 mb-6">
            {title.posterUrl && (
              <img
                src={title.posterUrl}
                alt={title.name}
                className="w-32 h-48 rounded-lg object-cover shadow-lg flex-shrink-0"
              />
            )}
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2">{title.name}</h2>
              {title.nameJa && title.nameJa !== title.name && (
                <p className="text-lg text-muted-foreground mb-2">{title.nameJa}</p>
              )}
              <div className="flex gap-2 mb-4 flex-wrap">
                <span className="px-2 py-1 bg-primary/10 text-primary rounded text-sm">
                  {title.mediaType === 'movie' ? '映画' : 'TV'}
                </span>
                <span className="px-2 py-1 bg-muted rounded text-sm">
                  {title.viewCount}回視聴
                </span>
              </div>
              {title.genres && title.genres.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {title.genres.map((genre) => (
                    <span key={genre} className="px-2 py-1 bg-muted rounded text-sm">
                      {genre}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          {title.overview && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2">あらすじ</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{title.overview}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 mb-6">
            {title.voteAverage !== null && title.voteAverage !== undefined && (
              <div>
                <span className="text-sm text-muted-foreground block mb-1">評価</span>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-bold text-yellow-500">★</span>
                  <span className="text-xl font-semibold">{Number(title.voteAverage).toFixed(1)}</span>
                  {title.voteCount && (
                    <span className="text-sm text-muted-foreground">({Number(title.voteCount).toLocaleString()}票)</span>
                  )}
                </div>
              </div>
            )}
            {title.runtime && (
              <div>
                <span className="text-sm text-muted-foreground block mb-1">再生時間</span>
                <p className="font-semibold">{title.runtime}分</p>
              </div>
            )}
            {title.releaseDate && (
              <div>
                <span className="text-sm text-muted-foreground block mb-1">リリース日</span>
                <p className="font-semibold">{new Date(title.releaseDate).toLocaleDateString('ja-JP')}</p>
              </div>
            )}
            {title.status && (
              <div>
                <span className="text-sm text-muted-foreground block mb-1">ステータス</span>
                <p className="font-semibold">{title.status}</p>
              </div>
            )}
            {title.numberOfSeasons && (
              <div>
                <span className="text-sm text-muted-foreground block mb-1">シーズン数</span>
                <p className="font-semibold">{title.numberOfSeasons}シーズン</p>
              </div>
            )}
            {title.numberOfEpisodes && (
              <div>
                <span className="text-sm text-muted-foreground block mb-1">エピソード数</span>
                <p className="font-semibold">{title.numberOfEpisodes}話</p>
              </div>
            )}
          </div>

          {title.cast && title.cast.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3">キャスト</h3>
              <div className="flex gap-3 overflow-x-auto pb-2">
                {title.cast.map((person, index) => (
                  <div key={index} className="flex-shrink-0 w-24 text-center">
                    {person.profile_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w185${person.profile_path}`}
                        alt={person.name}
                        className="w-24 h-24 rounded-full object-cover mb-2"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center mb-2">
                        <span className="text-2xl">👤</span>
                      </div>
                    )}
                    <p className="text-xs font-medium line-clamp-2">{person.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{person.character}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {title.crew && title.crew.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold mb-2">監督・スタッフ</h3>
              <div className="flex flex-wrap gap-2">
                {title.crew.map((person, index) => (
                  <span key={index} className="px-3 py-1 bg-muted rounded text-sm">
                    {person.name} ({person.job})
                  </span>
                ))}
              </div>
            </div>
          )}

          {title.trailerKey && (
            <div className="mb-6">
              <h3 className="font-semibold mb-3">予告編</h3>
              <div className="aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${title.trailerKey}`}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded-lg"
                ></iframe>
              </div>
            </div>
          )}

          <div className="space-y-2 mb-6 text-sm">
            <div>
              <span className="text-muted-foreground">初回視聴:</span>
              <span className="ml-2">{new Date(title.firstWatched).toLocaleDateString('ja-JP')}</span>
            </div>
            <div>
              <span className="text-muted-foreground">最終視聴:</span>
              <span className="ml-2">{new Date(title.lastWatched).toLocaleDateString('ja-JP')}</span>
            </div>
            {title.tmdbId && (
              <div>
                <span className="text-muted-foreground">TMDb ID:</span>
                <span className="ml-2">{title.tmdbId}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3 justify-between">
            <div className="flex gap-3">
              <button
                onClick={handleRefresh}
                disabled={isRefreshing || !title.tmdbId}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                情報を更新
              </button>
              <button
                onClick={onOpenSearch}
                className="flex items-center gap-2 px-4 py-2 border border-border rounded hover:bg-muted"
              >
                <Search className="h-4 w-4" />
                別の作品を検索
              </button>
            </div>
            <button
              onClick={handleDelete}
              className="flex items-center gap-2 px-4 py-2 border border-red-500 text-red-500 rounded hover:bg-red-500/10"
            >
              <Trash2 className="h-4 w-4" />
              削除
            </button>
          </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
